<template>
  <div>
    <Pane />
    <a-card class="container">
      <div class="toolbar">
        <a-form-model layout="inline" @keyup.enter.native="query">
          <a-form-model-item label="工单编号">
            <a-input v-model="form.orderNo" placeholder="请输入工单编号" style="width: 180px" allowClear />
          </a-form-model-item>
          <a-form-model-item label="标题">
            <a-input v-model="form.title" placeholder="请输入标题" style="width: 180px" allowClear />
          </a-form-model-item>
          <a-form-model-item label="工单状态">
            <a-select v-model="form.status" placeholder="全部" style="width: 140px" allowClear>
              <a-select-option v-for="item in statusList" :key="item.value" :value="item.value">
                {{ item.name }}
              </a-select-option>
            </a-select>
          </a-form-model-item>
          <a-form-model-item label="优先级">
            <a-select v-model="form.priority" placeholder="全部" style="width: 120px" allowClear>
              <a-select-option v-for="item in priorityList" :key="item.value" :value="item.value">
                {{ item.name }}
              </a-select-option>
            </a-select>
          </a-form-model-item>
          <!-- 已处理工单支持按申请时间范围筛选（精确到年月日） -->
          <a-form-model-item v-if="mode === 'processed'" label="申请时间">
            <a-range-picker
              :value="dateRange"
              style="width: 250px"
              separator="至"
              :placeholder="['开始日期', '结束日期']"
              @change="onDateRangeChange"
            />
          </a-form-model-item>
          <a-form-model-item>
            <a-space>
              <a-button type="primary" @click="query">查询</a-button>
              <a-button @click="reset">重置</a-button>
            </a-space>
          </a-form-model-item>
        </a-form-model>
        <!-- 右侧操作区：已处理工单显示导出按钮（复用当前查询条件，所见即所得），其余场景透传 extra-action 槽 -->
        <a-space class="toolbar-extra">
          <a-button v-if="mode === 'processed'" :loading="exporting" @click="exportDone">导出</a-button>
          <slot name="extra-action" />
        </a-space>
      </div>

      <Padding />

      <a-table
        bordered
        :row-selection="rowSelection"
        :columns="columns"
        :dataSource="list"
        :loading="loading"
        :customRow="customRow"
        @change="onChange"
        :pagination="{
          total,
          current,
          pageSize,
          showTotal: (total) => `共 ${total} 条记录`,
        }"
      >
        <template slot="statusCol" slot-scope="text">
          <a-tag :color="getStatusColor(text)">{{ getStatusName(text) }}</a-tag>
        </template>
        <template slot="priorityCol" slot-scope="text">
          <a-tag :color="text === 'urgent' ? 'red' : 'blue'">{{ getPriorityName(text) }}</a-tag>
        </template>
        <template slot="timeCol" slot-scope="text">
          {{ formatTime(text) }}
        </template>
        <template slot="actionCol" slot-scope="text, record">
          <!-- 行点击会跳转详情，行内操作需阻断冒泡 -->
          <a-space>
            <a
              v-if="mode === 'mine' && ['Processed', 'Rejected'].includes(record.status)"
              href="#"
              @click.prevent.stop="openApprovalDetail(record)"
            >确认完成</a>
            <a
              v-if="mode === 'mine' && record.status === 'Cancelled'"
              href="#"
              @click.prevent.stop="editOrder(record)"
            >更新</a>
            <a
              v-if="mode === 'pending'"
              href="#"
              @click.prevent.stop="openApprovalDetail(record)"
            >处理</a>
            <a
              v-if="mode === 'mine' && record.status === 'Cancelled'"
              href="#"
              @click.prevent.stop="reapplyOrder(record)"
            >重新发起</a>
          </a-space>
        </template>
      </a-table>
    </a-card>
  </div>
</template>

<script>
import moment from "moment";
import { saveAs } from "file-saver";
import watermark from "@/mixins/watermark";
import { exportDoneList } from "@/api/oa/workOrder";

export default {
  name: "workOrderListPanel",
  mixins: [watermark],

  props: {
    // mine-我的工单，pending-待处理工单，processed-已处理工单
    mode: { type: String, default: "mine" },
    // 列表查询函数
    fetchList: { type: Function, required: true },
  },

  data() {
    return {
      form: {},
      // 申请时间范围（moment对象数组，null为全部，仅已处理工单使用）
      dateRange: null,
      exporting: false,
      loading: false,
      current: 1,
      pageSize: 10,
      list: [],
      total: 0,
      selectedRowKeys: [],
      statusList: [
        { name: "处理中", value: "Processing" },
        { name: "已驳回", value: "Rejected" },
        { name: "已终止", value: "Cancelled" },
        { name: "处理完成", value: "Processed" },
        { name: "已关闭", value: "End" },
      ],
      priorityList: [
        { name: "普通", value: "common" },
        { name: "紧急", value: "urgent" },
      ],
      columns: [
        { title: "工单编号", dataIndex: "orderNo", width: 150 },
        { title: "标题", dataIndex: "title" },
        { title: "工单类型", dataIndex: "workType", align: "center", width: 100 },
        { title: "涉及模块", dataIndex: "workModule", align: "center", width: 110 },
        { title: "申请分类", dataIndex: "applyCategory", align: "center", width: 110 },
        {
          title: "工单状态",
          dataIndex: "status",
          align: "center",
          width: 100,
          scopedSlots: { customRender: "statusCol" },
        },
        {
          title: "优先级",
          dataIndex: "priority",
          align: "center",
          width: 80,
          scopedSlots: { customRender: "priorityCol" },
        },
        { title: "发起人", dataIndex: "applicantName", align: "center", width: 90 },
        { title: "处理组", dataIndex: "handlerDeptName", align: "center", width: 120 },
        { title: "工单处理人", dataIndex: "handlerName", align: "center", width: 100 },
        { title: "申请时间", dataIndex: "createAt", align: "center", width: 160, scopedSlots: { customRender: "timeCol" } },
        {
          title: "操作",
          align: "center",
          width: 180,
          scopedSlots: { customRender: "actionCol" },
        },
      ],
    };
  },

  computed: {
    rowSelection() {
      // 仅"我的工单"允许勾选（已终止工单重新发起用）
      if (this.mode !== "mine") {
        return null;
      }
      return {
        selectedRowKeys: this.selectedRowKeys,
        onChange: (keys) => {
          this.selectedRowKeys = keys;
          // 通知父组件当前勾选状态（用于控制"工单申请"按钮禁用）
          this.$emit("selection-change", keys);
        },
        getCheckboxProps: (record) => ({
          props: {
            disabled: record.status !== "Cancelled",
          },
        }),
      };
    },
  },

  mounted() {
    this.getList();
  },

  methods: {
    getStatusName(value) {
      const item = this.statusList.find((s) => s.value === value);
      return item ? item.name : value;
    },
    getStatusColor(value) {
      const map = {
        Processing: "blue",
        Rejected: "orange",
        Cancelled: "red",
        Processed: "cyan",
        End: "green",
      };
      return map[value] || "blue";
    },
    getPriorityName(value) {
      const item = this.priorityList.find((s) => s.value === value);
      return item ? item.name : value;
    },
    // 申请时间格式化为 YYYY-MM-DD HH:mm:ss
    formatTime(text) {
      return text ? moment(text).format("YYYY-MM-DD HH:mm:ss") : "";
    },
    // 行点击查看工单详情
    customRow(record) {
      return {
        on: { click: () => this.viewDetail(record) },
      };
    },
    viewDetail(record) {
      this.$router.push(`/oa/workOrder/detail?id=${record.id}`);
    },
    // 申请时间范围变更（moment对象数组或null）
    onDateRangeChange(dates) {
      this.dateRange = dates || null;
    },
    // 组装查询参数：form非空字段+申请时间范围（列表查询与导出共用，所见即所得）
    buildQueryParams() {
      const params = {};
      Object.keys(this.form).forEach((key) => {
        const value = this.form[key];
        if (value !== undefined && value !== null && value !== "") {
          params[key] = value;
        }
      });
      if (Array.isArray(this.dateRange) && this.dateRange.length === 2) {
        params.startDate = this.dateRange[0].format("YYYY-MM-DD");
        params.endDate = this.dateRange[1].format("YYYY-MM-DD");
      }
      return params;
    },
    // 导出已处理工单（复用当前查询条件，含时间范围）
    exportDone() {
      this.exporting = true;
      exportDoneList(this.buildQueryParams())
        .then((blob) => {
          saveAs(blob, `已处理工单_${moment().format("YYYYMMDDHHmmss")}.xlsx`);
        })
        .catch(() => {
          this.$message.error("导出失败，请稍后重试");
        })
        .finally(() => {
          this.exporting = false;
        });
    },
    getList() {
      const { current, pageSize } = this;
      this.loading = true;
      this.fetchList({
        pageNum: current,
        pageSize,
        ...this.buildQueryParams(),
      })
        .then((res) => {
          this.loading = false;
          if (Array.isArray(res.list)) {
            this.list = res.list;
            this.total = res.totalSize;
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },
    onChange(pagination) {
      this.current = pagination.current;
      this.pageSize = pagination.pageSize;
      this.getList();
    },
    query() {
      this.current = 1;
      this.getList();
    },
    reset() {
      this.form = {};
      this.dateRange = null;
      this.current = 1;
      this.getList();
    },
    // 打开审批详情（确认完成/处理）
    openApprovalDetail(record) {
      const taskId = (record.taskIds || "").split(",")[0];
      if (!taskId || !record.instanceId) {
        this.$message.error("该工单暂无待办任务");
        return;
      }
      this.$router.push(
        `/approval/task/detail?taskId=${taskId}&instanceId=${record.instanceId}`
      );
    },
    // 更新（仅已终止）
    editOrder(record) {
      this.$router.push(`/oa/workOrder/edit?id=${record.id}&mode=update`);
    },
    // 重新发起（仅已终止，行内操作）：复用原单走resubmit，不新生成工单
    reapplyOrder(record) {
      this.$router.push(`/oa/workOrder/edit?id=${record.id}&mode=resubmit`);
    },
    // 选中项重新发起（顶部按钮）
    reapplySelected() {
      if (this.selectedRowKeys.length !== 1) {
        this.$message.warning("请勾选一条已终止的工单");
        return;
      }
      const record = this.list.find((item) => item.id === this.selectedRowKeys[0]);
      if (!record || record.status !== "Cancelled") {
        this.$message.warning("请勾选一条已终止的工单");
        return;
      }
      this.reapplyOrder(record);
    },
  },
};
</script>

<style lang="less" scoped>
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  .toolbar-extra {
    flex-shrink: 0;
    margin-left: 16px;
  }
}

// 行可点击查看详情
::v-deep .ant-table-tbody > tr {
  cursor: pointer;
}
</style>
